import { motion } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

const faqs = [
  {
    question: '¿Cuánto tiempo tarda un envío?',
    answer: 'Los tiempos de entrega varían según el destino: Panamá 3-5 días, Venezuela 5-7 días, Colombia 4-6 días, y República Dominicana 4-6 días. Estos tiempos son aproximados y pueden variar según condiciones aduaneras.',
  },
  {
    question: '¿Cómo puedo rastrear mi paquete?',
    answer: 'Una vez que tu paquete sea recibido, te proporcionamos un número de tracking único. Puedes usarlo en nuestra sección de rastreo o contactarnos directamente por WhatsApp para conocer el estado de tu envío.',
  },
  {
    question: '¿Qué tipo de artículos puedo enviar?',
    answer: 'Puedes enviar ropa, zapatos, medicamentos (no controlados), artículos electrónicos, documentos, alimentos no perecederos, y más. Contáctanos para verificar si tu artículo específico puede ser enviado.',
  },
  {
    question: '¿Cuáles son las formas de pago?',
    answer: 'Aceptamos efectivo, transferencias bancarias, Zelle, y PayPal. También puedes pagar con tarjeta de crédito o débito en nuestra oficina.',
  },
  {
    question: '¿Ofrecen servicio de recogida a domicilio?',
    answer: 'Sí, ofrecemos servicio de recogida en domicilio en áreas seleccionadas. Consulta disponibilidad y tarifas contactándonos por WhatsApp o a través de nuestro formulario.',
  },
  {
    question: '¿Cómo se calculan las tarifas de envío?',
    answer: 'Las tarifas se calculan por peso y volumen del paquete, así como el destino. Puedes solicitar una cotización gratuita a través de nuestro formulario de contacto o vía WhatsApp.',
  },
  {
    question: '¿Qué pasa si mi paquete se daña o pierde?',
    answer: 'Todos nuestros envíos incluyen un seguro básico. También ofrecemos seguro adicional para paquetes de alto valor. En caso de cualquier incidencia, nuestro equipo te asistirá en el proceso de reclamo.',
  },
  {
    question: '¿Puedo enviar documentos importantes?',
    answer: 'Sí, ofrecemos servicio de mensajería para documentos importantes como pasaportes, documentos legales, y certificados. Estos envíos tienen manejo especial y seguimiento prioritario.',
  },
];

export const FAQ = () => {
  return (
    <section 
      id="faq" 
      className="relative py-24 md:py-32 bg-[#F8FAFC]"
      data-testid="faq-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left side - Mascot and intro */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-4 lg:sticky lg:top-32"
          >
            <span className="inline-flex items-center gap-2 bg-[#F5A623]/10 text-[#F5A623] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <HelpCircle className="w-4 h-4" />
              FAQ
            </span>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Preguntas <span className="text-[#0066CC]">Frecuentes</span>
            </h2>
            
            <p className="text-lg text-slate-600 mb-8">
              Resolvemos tus dudas más comunes. Si no encuentras lo que buscas, 
              no dudes en contactarnos.
            </p>
            
            {/* Curious Mascot */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-[#2DD4BF]/10 rounded-full blur-2xl" />
              <motion.img
                src={MASCOT_URL}
                alt="Mascota curiosa"
                className="relative w-40 h-40 object-contain mx-auto"
                animate={{ 
                  rotateY: [0, 10, 0, -10, 0],
                  scale: [1, 1.02, 1]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                data-testid="faq-mascot"
              />
            </div>
          </motion.div>
          
          {/* Right side - Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-8"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm px-6 overflow-hidden"
                  data-testid={`faq-accordion-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[#0066CC] transition-colors py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            
            {/* CTA after FAQ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 bg-gradient-to-r from-[#0066CC] to-[#2DD4BF] rounded-2xl p-8 text-white text-center"
            >
              <h3 className="text-xl font-bold mb-2">¿Tienes más preguntas?</h3>
              <p className="text-white/90 mb-4">
                Nuestro equipo está listo para ayudarte
              </p>
              <a
                href="#contacto"
                className="inline-block bg-white text-[#0066CC] font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors"
                data-testid="faq-contact-cta"
              >
                Contáctanos
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
